import axios from "axios";
import { env } from "./env";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { useCartStore } from "@/store/cart.store";

// Use relative URL on client to leverage Next.js rewrites/proxy (SameSite=Lax support)
const isBrowser = typeof window !== "undefined";
const baseURL = isBrowser ? "/api" : `${env.API_URL}/v1`;

export const axiosInstance = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
        "Content-Type": "application/json",
        "x-csrf-key": env.CSRF_KEY,
    },
});

// Avoid infinite refresh loops
let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

axiosInstance.interceptors.request.use((config) => {
    const sessionId = useCartStore.getState().sessionId;
    if (sessionId) {
        config.headers["x-session-id"] = sessionId;
    }
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        const originalRequest = config;

        if (response?.status === 401 && !originalRequest._retry) {
            // Exclude only login and refresh to avoid infinite loops
            // Note: /auth/me is intentionally NOT excluded so startup recovery works
            const isExcludedRoute =
                originalRequest.url?.includes("/auth/login") ||
                originalRequest.url?.includes("/auth/refresh");

            if (isExcludedRoute) {
                useAuthStore.getState().clearUser();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        return axiosInstance(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Refresh using the refreshToken HttpOnly cookie (sent automatically via withCredentials)
                // The backend sets a new accessToken cookie via Set-Cookie on the response
                await axiosInstance.post(`/auth/refresh`);

                isRefreshing = false;
                processQueue(null, "");

                return axiosInstance(originalRequest);
            } catch (refreshError: unknown) {
                isRefreshing = false;
                processQueue(refreshError, null);
                useAuthStore.getState().clearUser();

                // Only redirect to login on admin pages to avoid disrupting public pages
                if (isBrowser) {
                    const pathname = window.location.pathname;
                    if (pathname.startsWith("/admin")) {
                        window.location.href = "/auth/login";
                    }
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);
